"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.applySnakeCase = applySnakeCase;
exports.create = create;
exports.createTdataStr = createTdataStr;
exports.default = void 0;
exports.props = props;
exports.stringifyArray = stringifyArray;
exports.updateTracking = updateTracking;
exports.useTracking = useTracking;
exports.useTrackingUpdate = useTrackingUpdate;
var _react = require("react");
var _base = require("./base.functions");
/**
 * useTracking a method to abstract out the integration with SWP FOS TrackingData.js
 * @param {object} ref created by React.useRef containing the element to be tracked.
 * Element should have `data-track-name` for TrackingData to do anything.
 * window.cms.trackingData.addTrackedElement should handle the rebind decisions
 */
function useTracking(ref) {
  (0, _react.useEffect)(() => {
    if (typeof window !== 'undefined') {
      if (ref?.current && ref.current instanceof HTMLElement) {
        window?.cms?.trackingData?.addTrackedElement?.(ref.current);
      }
    }
  }, [ref?.current]);
}

/**
 * updateTracking a method to abstract out the integration with SWP FOS TrackingData.js
 * @param {object} ele HTML element that needs the data-track-name & data-eid updated
 * @param {string} newTrackName string to be used for the updated track name and eid
 * @param {string} newTrackId optional string to be used for the updated track promo id
 * Element should have `data-eid` already defined as an attribute for updatedTrackingNameInEid to work correctly.
 * window.cms.trackingData.updatedTrackingNameInEid
 * newTrackId if not provided it will default to the exiting element identifier
 */
function updateTracking(ele, newTrackName, newTrackId) {
  if (typeof window !== 'undefined') {
    if (ele && ele instanceof HTMLElement && newTrackName) {
      window?.cms?.trackingData?.updatedTrackingNameInEid?.(ele, newTrackName);
      window?.cms?.trackingData?.updatePromoId?.(ele, newTrackId);
    }
  }
}

/**
 * useTrackingUpdate - A method to abstract out the integration with SWP FOS TrackingData.js.
 * @param {object} el - React useRef object containing the element to be tracked.
 * @returns {object} - Returns the forwarded reference to the element for tracking update.
 *
 * updateTracking relies on the getClickable function to update button, or anchors to be tracked
 * The element should have `data-track-name` attribute for TrackingData to take any action.
 * The responsibility of handling rebind decisions lies with `window.cms.trackingData.addTrackedElement`.
 */
function useTrackingUpdate(el) {
  const didMountRef = (0, _react.useRef)(false);
  const ctaRef = (0, _react.useRef)(null);
  (0, _react.useEffect)(() => {
    if (didMountRef.current && el?._name) {
      updateTracking((0, _base.getClickable)({
        ref: ctaRef
      }), el._name);
    } else {
      didMountRef.current = true;
    }
  }, [el?._name]);
  return {
    ctaRef
  };
}

/**
 * Applies snake case naming to a string
 * @param {string} str The string to apply snake case naming to
 * @returns {string} A processed, snake case named string
 *
 * Accepted characters within an e_ID are:
 * lowercase alphanumeric. Uppercase alphanumeric characters will be converted to lowercase.
 * "_" and "-" for composite words
 * "/" and "-" for the path and section (allow URI characters)
 * "." between e_id components
 */
function applySnakeCase(str) {
  // return str?.toLowerCase().split(' ').join('_');
  if (str) {
    let trackingName = str.toLowerCase();
    // replace space with underscore
    trackingName = trackingName.replace(/\s+/g, '_');
    // remove all non-alphanumeric characters except for _ - / .
    trackingName = trackingName.replace(/[^a-z0-9_\-/.]/g, '');
    // remove all duplicate characters
    trackingName = trackingName.replace(/\/+/g, '/');
    trackingName = trackingName.replace(/\.+/g, '.');
    trackingName = trackingName.replace(/-+/g, '-');
    trackingName = trackingName.replace(/_+/g, '_');
    return trackingName;
  }
}

/**
 * Builds a comma-delimited string from array, while applying snake case naming to a each array element
 * @param {array} arr The array to convert to a string
 * @returns {string} A processed, comma-delimited, snake case named string
 */
function stringifyArray(arr) {
  return arr?.map(str => applySnakeCase(str)).join('¬');
}

/**
 * Encodes custom properties object into a string for the data-tdata attribute
 * @param {object} customProperties custom properties to encode in data-tdata value
 * @returns {object} data-tdata attribute and value
 */
function createTdataStr(customProperties) {
  const tdata = [];
  for (const prop in customProperties) {
    if (Array.isArray(customProperties[prop])) {
      tdata.push(`${prop},${customProperties[prop].join('¬')}`);
    } else {
      tdata.push(`${prop},${customProperties[prop]}`);
    }
  }
  return {
    ['data-tdata']: tdata.join('^')
  };
}

/**
 * a shortcut to build the props
 * @param {string} contentName the name of the content item
 * @param {boolean} [clickable=false] is it a clickable interaction
 * @param {boolean} [promo=false] is it a promotion or regular eid
 * @param {object} customProperties custom properties data-tdata
 * @returns {object} a props object should be merged onto a html element rendered on the screen
 */
function props(contentName, clickable, promo, customProperties = {}) {
  const trackType = `data-track-${promo ? 'promo' : 'eid'}-${clickable ? 'click' : 'impression'}`;
  return {
    ['data-track-name']: applySnakeCase(contentName),
    [trackType]: '',
    ...(Object.keys(customProperties).length > 0 && createTdataStr(customProperties))
  };
}

/**
 * a shortcut to build the props and useTracking
 * @param {string} contentName the name of the content item
 * @param {boolean} [clickable=false] is it a clickable interaction
 * @param {boolean} [promo=false] is it a promotion or regular eid
 * @param {object} customProperties custom properties data-tdata
 * @returns {object} a props object should be merged onto a html element rendered on the screen complete with tracking hook
 */
function create(contentName, clickable, promo, customProperties = {}) {
  /* eslint-disable react-hooks/rules-of-hooks */
  const ref = (0, _react.useRef)();
  useTracking(ref);
  /* eslint-enable react-hooks/rules-of-hooks */

  return {
    ...props(contentName, clickable, promo, customProperties),
    ref
  };
}
var _default = exports.default = {
  create,
  props,
  useTracking,
  useTrackingUpdate,
  updateTracking,
  applySnakeCase,
  stringifyArray
};
/**
 * example usage
 * import tracking from './tracking';
 * const styledDiv = styled.div`magiccss`;
 * function PaymentsMarquee3({ _name, ...props }) {
 *  const trackingProps = tracking.create(_name);
 *
 *  return <styledDiv {...trackingProps}>magic goes here</styledDiv>
 * }
 *  */